import type {
  PresenceEntry,
  PresenceSelection,
} from '@pypestream/pro-studio-collab-client';

import type { SolutionUserPresenceItem } from '../xstate/prostudio.xstate.types';

/**
 * Presence under live editing (PSTUDIO-4451) comes from the collab document's
 * awareness instead of the Hasura presence table. These two functions are the
 * seam: collaborators are reshaped into the presence items the editor already
 * renders (cell focus, avatars, the publish modal's coworker list), and the
 * table's selected cell into the selection the document shares.
 *
 * The one column whose name differs between the two (`cssClassname` in the
 * table, `cssClassName` in the document) is mapped both ways. A selection with
 * no field is a row; it shows on the row's number cell.
 */
const TABLE_TO_DOC_FIELD: Record<string, string> = {
  cssClassname: 'cssClassName',
};
const DOC_TO_TABLE_FIELD: Record<string, string> = {
  cssClassName: 'cssClassname',
};

// A coworker's avatar URL comes from the shared document; anything but https
// would let a collaborator make every viewer fetch an address of their choosing.
const safeAvatar = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
};

export const collabPresenceItems = (
  presence: readonly PresenceEntry[],
  pagePath: string
): SolutionUserPresenceItem[] => {
  // One item per person, as the Hasura presence had: a coworker with two tabs
  // is one avatar — the tab with a selection, so their cursor still shows.
  const byUser = new Map<string, PresenceEntry>();
  for (const entry of presence) {
    const seen = byUser.get(entry.userId);
    if (!seen || (!seen.selection && entry.selection)) {
      byUser.set(entry.userId, entry);
    }
  }
  return [...byUser.values()].map((entry) => {
    const { selection } = entry;
    const field = selection?.field ?? 'nodeNumber';
    return {
      id: String(entry.clientId),
      firstSeenAt: null,
      lastSeenAt: null,
      isActive: true,
      pagePath,
      solutionId: '',
      userId: entry.userId,
      user: {
        id: entry.userId,
        firstName: entry.displayName,
        lastName: '',
        picture: safeAvatar(entry.avatarUrl),
      },
      pageParams: selection
        ? {
            focusedCell: {
              rowId: selection.nodeId,
              columnId: DOC_TO_TABLE_FIELD[field] ?? field,
            },
          }
        : null,
    };
  });
};

export const toPresenceSelection = (
  cell: { rowId: string; columnId: string } | undefined | null
): PresenceSelection | undefined =>
  cell
    ? {
        nodeId: cell.rowId,
        field: TABLE_TO_DOC_FIELD[cell.columnId] ?? cell.columnId,
      }
    : undefined;

/**
 * The presence list the editor renders: the document's collaborators while
 * the table is collab-fed (`live` is the store), the Hasura presence otherwise.
 */
export const pickSolutionPresences = (
  legacy: SolutionUserPresenceItem[] | undefined,
  live: { presence: readonly PresenceEntry[] } | null | undefined,
  pagePath: string
): SolutionUserPresenceItem[] | undefined =>
  live ? collabPresenceItems(live.presence, pagePath) : legacy;

/**
 * What the editor renders, given both lists (PSTUDIO-4449).
 *
 * On the Workflows page, the live document's collaborators — they give the
 * fast cursors. Its awareness carries no page, though, and the session is open
 * on every page of the solution, so each collaborator is put on the page the
 * Hasura presence (still written by every page) has for them: the publish
 * warning keeps everyone in the solution, and a "viewing this page" filter
 * keeps only those on it. One Hasura has not seen yet is on Workflows if they
 * share a cursor (only Workflows shares one), else on no page. Anywhere else,
 * the Hasura list as it is.
 *
 * E2E only: a test that froze presence (`__PS_E2E_FREEZE_PRESENCE__`, see
 * `use-solution-presence.ts`) injects its fixtures into the context and owns
 * the list.
 */
export const presencesToRender = ({
  legacy,
  live,
  testOwned,
  onWorkflows,
  pagePath,
}: {
  legacy: SolutionUserPresenceItem[] | undefined;
  live: SolutionUserPresenceItem[] | null;
  testOwned: boolean;
  onWorkflows: boolean;
  pagePath: string;
}): SolutionUserPresenceItem[] | undefined => {
  if (testOwned || !onWorkflows || !live) return legacy;
  // One Hasura row per tab: any tab of theirs on this page puts them here.
  const pagesOf = new Map<string | undefined, string[]>();
  for (const presence of legacy ?? []) {
    pagesOf.set(presence.userId, [
      ...(pagesOf.get(presence.userId) ?? []),
      presence.pagePath,
    ]);
  }
  const pageOf = (presence: SolutionUserPresenceItem) => {
    const known = pagesOf.get(presence.userId);
    if (known?.includes(pagePath)) return pagePath;
    if (known?.length) return known[0];
    return presence.pageParams?.focusedCell ? pagePath : '';
  };
  return live.map((presence) => ({ ...presence, pagePath: pageOf(presence) }));
};
